import React, { useEffect, useState } from 'react';
import { ServiceRegistry } from '../../core/ServiceRegistry';

/**
 * ProjectileTrackerPanelContent — kit tool `projectileTracker`. Live table of
 * active projectiles (id/type/pos/vel/target/homing) from ProjectileSystem,
 * polled at 10Hz. Zero-prop: the 🛠 kit's ToolWindow supplies the window chrome.
 *
 * Summoned three ways, all converging on the kit: the launcher tile, the
 * `/tracker` chat command, and the DEV-BAR button — the latter two via
 * debugToolsBridge.toggle('projectileTracker'). (Migrated off the UIContext
 * bridge into the kit, 2026-05-30.)
 */

interface ProjectileData {
  id: string;
  type: string;
  pos: { x: number; y: number };
  vel: { x: number; y: number };
  target: string;
  homing: boolean;
  age: number;
}

export const ProjectileTrackerPanelContent: React.FC = () => {
  const [projectiles, setProjectiles] = useState<ProjectileData[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      const registry = ServiceRegistry.getInstance();
      const entitySystem = registry.get('EntitySystem');
      const projSystem = entitySystem?.getProjectileSystem();
      if (projSystem) {
        setProjectiles(projSystem.getTelemetry());
      }
    }, 100); // 10hz update rate
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      height: '100%',
      overflowY: 'auto',
      color: '#00FF00',
      fontFamily: 'monospace',
      fontSize: '12px',
      background: 'rgba(0, 0, 0, 0.9)',
      padding: '8px',
    }}>
      {projectiles.length === 0 ? (
        <div style={{ textAlign: 'center', opacity: 0.5, marginTop: '20px' }}>
          No Active Projectiles
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(0,255,0,0.3)' }}>
              <th>ID</th>
              <th>Pos</th>
              <th>Vel</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {projectiles.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid rgba(0,255,0,0.1)' }}>
                <td title={p.id} style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.type === 'special' ? '🔥' : '🔹'} {p.id.split('_')[2]}...
                </td>
                <td>{p.pos.x},{p.pos.y}</td>
                <td>{p.vel.x},{p.vel.y}</td>
                <td style={{ color: p.homing ? '#FF00FF' : 'inherit' }}>
                  {p.target !== 'None' ? `🎯 ${p.target}` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '10px', fontSize: '10px', opacity: 0.7, borderTop: '1px solid rgba(0,255,0,0.3)', paddingTop: '4px' }}>
        Active Count: {projectiles.length} | Update Rate: 10Hz
      </div>
    </div>
  );
};
